import { useCallback, useEffect, useState } from "react";
import { Calendar, Clock, Warning, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { debtorApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Spinner from "@/components/Spinner";
import AgendaGroup from "@/components/agenda/AgendaGroup";
import { fmtCurrency } from "@/lib/format";

const CATEGORIAS = [
  { key: "vencida", label: "Vencidas", tone: "bg-destructive/10 border-destructive/30 text-destructive", icon: Warning },
  { key: "hoy", label: "Hoy", tone: "bg-warning/10 border-warning/30 text-warning", icon: Clock },
  { key: "manana", label: "Mañana", tone: "bg-chart-3/10 border-chart-3/30 text-chart-3", icon: BellRinging },
  { key: "futuro", label: "Próximas", tone: "bg-primary/5 border-primary/20 text-primary", icon: Calendar },
];

function AgendaEmptyState() {
  return (
    <div className="bg-card border border-border rounded-md p-12 text-center" data-testid="empty-agenda">
      <div className="w-16 h-16 rounded-md bg-primary/10 grid place-items-center mx-auto mb-5 text-primary">
        <Calendar size={30} weight="duotone" />
      </div>
      <h3 className="font-heading text-xl font-semibold">Tu agenda está limpia</h3>
      <p className="text-muted-foreground mt-2 max-w-md mx-auto">
        Aún no hay promesas de pago registradas. Cuando un deudor confirme una fecha,
        aparecerá aquí organizada por urgencia.
      </p>
    </div>
  );
}

function AgendaHeader({ count, totalComprometido, tomorrowCount, sending, onReminder }) {
  const disabled = sending || tomorrowCount === 0;
  const title = tomorrowCount === 0 ? "No hay promesas para mañana" : "";
  return (
    <div className="rounded-md border border-border bg-card p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div>
        <div className="overline">Próximos vencimientos</div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight mt-0.5">
          Agenda de promesas
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {count} compromiso(s) activos · Monto comprometido{" "}
          <span className="font-mono font-semibold text-foreground">
            {fmtCurrency(totalComprometido)}
          </span>
        </p>
      </div>
      <Button onClick={onReminder} disabled={disabled} data-testid="run-reminder-btn" title={title}>
        {sending ? (
          <Spinner size="s" className="mr-2" />
        ) : (
          <BellRinging size={16} weight="duotone" className="mr-2" />
        )}
        Enviar recordatorios de mañana ({tomorrowCount})
      </Button>
    </div>
  );
}

export default function AgendaPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await debtorApi.agenda());
    } catch {
      toast.error("No se pudo cargar la agenda");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runReminder = async () => {
    setSending(true);
    try {
      const r = await debtorApi.runPreventiveReminder();
      if (r.processed_count === 0) {
        toast.info("No hay promesas que venzan mañana");
      } else {
        toast.success(`${r.processed_count} recordatorio(s) de cortesía generados`);
      }
      load();
    } catch {
      toast.error("Error ejecutando recordatorios preventivos");
    } finally {
      setSending(false);
    }
  };

  const grouped = CATEGORIAS.map((c) => ({
    ...c,
    rows: items.filter((i) => i.categoria === c.key),
  }));
  const totalComprometido = items.reduce((acc, i) => acc + Number(i.promesa_monto || 0), 0);
  const tomorrowCount = grouped.find((g) => g.key === "manana")?.rows.length || 0;

  let content;
  if (loading) {
    content = <div className="p-12 text-center text-muted-foreground">Cargando agenda…</div>;
  } else if (items.length === 0) {
    content = <AgendaEmptyState />;
  } else {
    content = (
      <div className="space-y-8">
        {grouped.map((g) => <AgendaGroup key={g.key} group={g} />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <AgendaHeader
        count={items.length}
        totalComprometido={totalComprometido}
        tomorrowCount={tomorrowCount}
        sending={sending}
        onReminder={runReminder}
      />
      {content}
    </div>
  );
}
